import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Dropdown,
  Layout,
  Menu,
  message,
} from "antd";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  HomeOutlined,
  PlusOutlined,
  LogoutOutlined,
  SearchOutlined,
  UserOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  BookOutlined,
  BellOutlined,
  FolderOutlined,
  MessageOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CarOutlined,
  IssuesCloseOutlined,
  CalendarOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { auth, firestore } from "../firebase";
import { useAuditoria } from "../hooks/useAuditoria";
import { useAuth } from "./Context";
import BranchIcon from "../img/icons/branch.svg?react";
import EmpleyeeIcon from "../img/icons/empleado.svg?react";
import HolidayIcon from "../img/icons/holiday.svg?react";
import RHIcon from "../img/icons/rh.svg?react";
import ConcretoIcon from "../img/icons/concreto.svg?react";
import GasStationIcon from "../img/icons/gas-station.svg?react";
import PositionIcon from "../img/icons/position.svg?react";
import PermissionIcon from "../img/icons/permission.svg?react";
import MetalIcon from "../img/icons/metal.svg?react";
import { NotificationProvider } from "./NotificationContext";
import { NotificacionesProvider } from "./notificaciones/NotificacionesContext";
import { NotificationBell } from "./notificaciones";
import NoPermisos from "./NoPermisos";
import { Spin } from "antd";

const { Header, Content, Footer, Sider } = Layout;

const AdminLayout = () => {
  const [employeeData, setEmployeeData] = useState({ puesto: null, areas: [] });
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const {
    currentUser,
    role,
    sucursal,
    permisos,
    setPermisos,
    setIsAdmin,
    isAdmin,
    loading,
  } = useAuth();
  const { auditarLogout } = useAuditoria();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [isBirthday, setIsBirthday] = useState(false);

  const handleMenuClick = () => {
    setVisible(!visible);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleLogout = async () => {
    try {
      // Intentar registrar el logout en auditoría antes de cerrar sesión
      try {
        await auditarLogout();
      } catch (error) {
        console.error('Error al registrar logout en auditoría:', error);
      }
      
      await auth.signOut();
      message.success("Sesión cerrada con éxito.");
      navigate("/");
    } catch (error) {
      console.error("Error al cerrar sesión:", error.message);
    }
  };

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path.includes('/sistema/bienvenida')) return ['bienvenida'];
    if (path.includes('/sistema/perfil')) return ['perfil'];
    if (path.includes('/sistema/sucursal')) return ['sucursal'];
    if (path.includes('/sistema/agenda')) return ['agenda'];
    if (path.includes('/sistema/empleado/buscar')) return ['buscarEmpleado'];
    if (path.includes('/sistema/empleado/agregar')) return ['agregarEmpleado'];
    if (path.includes('/sistema/cliente/buscar')) return ['buscarCliente'];
    if (path.includes('/sistema/cliente/agregar')) return ['agregarCliente'];
    // Agregar más rutas según sea necesario
    return ['bienvenida']; // valor por defecto
  };

  const handleDelete = (key) => {
    // ...
  };

  const notifications = [
    {
      key: 1,
      title: "Nuevo cotización asignada",
      description: "Esta es la descripción de la notificación 1.",
    },
    {
      key: 2,
      title: "Proyecto CCG0102",
      description: "Cotización pendiente.",
    },
  ];

  const notificationMenu = (
    <Menu className="notifications-content">
      {notifications.map((notification, index) => (
        <Menu.Item key={notification.key}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>{notification.title}</strong>
              <p>{notification.description}</p>
            </div>
            <Button
              danger
              type="text"
              onClick={() => handleDelete(notification.key)}
              icon={<DeleteOutlined />}
            ></Button>
          </div>
          {index < notifications.length - 1 && <hr />}
        </Menu.Item>
      ))}
    </Menu>
  );

  useEffect(() => {
    if (!currentUser || !sucursal) return;

    setIsAdmin(
      role === "Director General" || role === "Gerente General" ? true : false
    );

    // Validar que currentUser.uid y sucursal existan antes de llamar a .doc()
    if (!currentUser.uid || !sucursal) return;

    const unsubscribe = firestore
      .collection("sucursal")
      .doc(sucursal)
      .collection("empleado")
      .doc(currentUser.uid)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            setEmployeeData({
              puesto: data.puesto || "",
              areas: data.areas || [],
            });
          } else {
            // ...
          }
        },
        (error) => {
          console.error("Error getting document:", error);
        }
      );

    return () => unsubscribe();
  }, [currentUser, role, setIsAdmin, sucursal]);

  useEffect(() => {
    if (!employeeData.puesto && employeeData.areas.length === 0) {
      return;
    }

    // Utilidad para merge profundo de objetos
    function deepMerge(target, source) {
      for (const key of Object.keys(source)) {
        if (
          source[key] &&
          typeof source[key] === 'object' &&
          !Array.isArray(source[key])
        ) {
          if (!target[key]) target[key] = {};
          deepMerge(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
      return target;
    }

    const fetchPermisos = async () => {
      try {
        let permisosFinal = {};
        // Validar que sucursal y puesto existan
        if (!sucursal || !employeeData.puesto) return;
        // Permisos del puesto
        const puestoSnapshot = await firestore
          .collection("sucursal")
          .doc(sucursal)
          .collection("puesto")
          .doc(employeeData.puesto)
          .collection("configuracion")
          .doc("permisos")
          .get();
        if (puestoSnapshot.exists) {
          permisosFinal = deepMerge(permisosFinal, puestoSnapshot.data());
        }
        // Permisos de cada área
        if (employeeData.areas && employeeData.areas.length > 0) {
          for (const area of employeeData.areas) {
            if (!area) continue;
            const areaSnapshot = await firestore
              .collection("sucursal")
              .doc(sucursal)
              .collection("puesto")
              .doc(employeeData.puesto)
              .collection("areas")
              .doc(area)
              .collection("configuracion")
              .doc("permisos")
              .get();
            if (areaSnapshot.exists) {
              deepMerge(permisosFinal, areaSnapshot.data());
            }
          }
        }
        setPermisos(permisosFinal);
      } catch (error) {
          console.error("Error fetching permisos:", error);
      }
    };

    if (!isAdmin) fetchPermisos();
  }, [employeeData.puesto, employeeData.areas, sucursal, employeeData, isAdmin, setPermisos]);

  return (
    <NotificationProvider>
      <NotificacionesProvider>
        <Layout style={{ minHeight: "100vh", maxHeight: "100vh", background: "#283891" }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={toggleCollapsed}
          style={{
            background: "#fff",
            marginTop: "64px",
            maxHeight: "86vh",
            overflowX: "scroll",
          }}
        >
          <Menu theme="light" selectedKeys={getSelectedKeys()} mode="inline" >
            {(isAdmin || permisos.bienvenida) && (
              <Menu.Item key="bienvenida" icon={<HomeOutlined />}>
                <Link to="/sistema/bienvenida">Inicio</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.perfil) && (
              <Menu.Item key="perfil" icon={<UserOutlined />}>
                <Link to="/sistema/perfil">Perfil</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.sucursales) && (
              <Menu.Item key="sucursal" icon={<BranchIcon />}>
                <Link to="/sistema/sucursales">Sucursales</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.agenda) && (
              <Menu.Item key="agenda" icon={<CalendarOutlined />}>
                <Link to="/sistema/agenda">Agenda</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.empleado) && (
              <Menu.SubMenu
                key="empleados"
                icon={<EmpleyeeIcon />}
                title="Empleados"
              >
                {(isAdmin || permisos.empleado.buscar) && (
                  <Menu.Item key="buscarEmpleado" icon={<SearchOutlined />}>
                    <Link to="/sistema/empleado/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.empleado.agregar) && (
                  <Menu.Item key="agregarEmpleado" icon={<PlusOutlined />}>
                    <Link to="/sistema/empleado/agregar">Agregar</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.cliente) && (
              <Menu.SubMenu
                key="clilentes"
                icon={<UserSwitchOutlined />}
                title="Clientes"
              >
                {(isAdmin || permisos.cliente.buscar) && (
                  <Menu.Item key="buscarCliente" icon={<SearchOutlined />}>
                    <Link to="/sistema/cliente/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.cliente.agregar) && (
                  <Menu.Item key="agregarCliente" icon={<PlusOutlined />}>
                    <Link to="/sistema/cliente/agregar">Agregar</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.proyecto) && (
              <Menu.SubMenu
                key="proyecto"
                icon={<FolderOutlined />}
                title="Proyectos"
              >
                {(isAdmin || permisos.proyecto.concreto) && (
                  <Menu.SubMenu
                    key="concreto"
                    icon={<ConcretoIcon />}
                    title="Concreto"
                  >
                    {(isAdmin || permisos.proyecto.concreto.buscar) && (
                      <Menu.Item
                        key="buscarProyectoConcreto"
                        icon={<SearchOutlined />}
                      >
                        <Link to="/sistema/proyectos/concreto/buscar">
                          Buscar
                        </Link>
                      </Menu.Item>
                    )}
                    {(isAdmin || permisos.proyecto.concreto.agregar) && (
                      <Menu.Item
                        key="agregarProyectoConcreto"
                        icon={<PlusOutlined />}
                      >
                        <Link to="/sistema/proyectos/concreto/agregar">
                          Agregar
                        </Link>
                      </Menu.Item>
                    )}
                  </Menu.SubMenu>
                )}
                {(isAdmin || permisos.proyecto.estructura) && (
                  <Menu.SubMenu
                    key="estructura"
                    icon={<MetalIcon />}
                    title="Estructura"
                  >
                    {(isAdmin || permisos.proyecto.estructura.buscar) && (
                      <Menu.Item
                        key="buscarProyectoEstructura"
                        icon={<SearchOutlined />}
                      >
                        <Link to="/sistema/proyectos/estructura/buscar">
                          Buscar
                        </Link>
                      </Menu.Item>
                    )}
                    {(isAdmin || permisos.proyecto.estructura.agregar) && (
                      <Menu.Item
                        key="agregarProyectoEstructura"
                        icon={<PlusOutlined />}
                      >
                        <Link to="/sistema/proyectos/estructura/agregar">
                          Agregar
                        </Link>
                      </Menu.Item>
                    )}
                  </Menu.SubMenu>
                )}
                {(isAdmin || permisos.proyecto.agregar) && (
                  <Menu.Item
                    key="agregarProyectoAregar"
                    icon={<PlusOutlined />}
                  >
                    <Link to="/sistema/proyectos/agregar">
                      Agregar
                    </Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.conversaciones) && (
              <Menu.Item key="conversacion" icon={<MessageOutlined />}>
                <Link to="/sistema/chat">Conversaciones</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.capacitacion) && (
              <Menu.SubMenu
                key="capacitacion"
                icon={<BookOutlined />}
                title="Biblioteca"
              >
                {(isAdmin || permisos.capacitacion.normas) && (
                  <Menu.Item key="buscarNorma" icon={<SearchOutlined />}>
                    <Link to="/sistema/capacitacion/norma/buscar">Normas</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.capacitacion.procedimientos) && (
                  <Menu.Item
                    key="buscarProcedimiento"
                    icon={<SearchOutlined />}
                  >
                    <Link to="/sistema/capacitacion/procedimiento/buscar">
                      Procedimientos
                    </Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.capacitacion.buscar) && (
                  <Menu.Item key="buscarBibliotecas" icon={<SearchOutlined />}>
                    <Link to="/sistema/capacitacion/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.capacitacion.agregar) && (
                  <Menu.Item key="agregarCapacitacion" icon={<PlusOutlined />}>
                    <Link to="/sistema/capacitacion/agregar">Agregar</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}

            {(isAdmin || permisos.solicitudes) && (
              <Menu.Item key="solicitudes" icon={<IssuesCloseOutlined />}>
                <Link to="/sistema/solicitud">Solicitudes</Link>
              </Menu.Item>
            )}
            {(isAdmin || permisos.vehiculos) && (
              <Menu.SubMenu key="cars" icon={<CarOutlined />} title="Vehiculos">
                {(isAdmin || permisos.vehiculos.buscar) && (
                  <Menu.Item key="buscarCarro" icon={<SearchOutlined />}>
                    <Link to="/sistema/vehiculo/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.vehiculos.agregar) && (
                  <Menu.Item key="agregarCarro" icon={<PlusOutlined />}>
                    <Link to="/sistema/vehiculo/agregar">Agregar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.vehiculos.carga) && (
                  <Menu.Item
                    key="agregarCargaGasolina"
                    icon={<GasStationIcon />}
                  >
                    <Link to="/sistema/vehiculo/carga-gas">
                      Registrar Carga
                    </Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.vehiculos.carga) && (
                  <Menu.Item
                    key="historialCargas"
                    icon={<ClockCircleOutlined />}
                  >
                    <Link to="/sistema/vehiculo/historial-cargas">
                      Historial Cargas
                    </Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.equipos) && (
              <Menu.SubMenu key="equipment" icon={<ToolOutlined />} title="Equipos">
                {(isAdmin || permisos.equipos.buscar) && (
                  <Menu.Item key="buscarEquipo" icon={<SearchOutlined />}>
                    <Link to="/sistema/equipo/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                {(isAdmin || permisos.equipos.agregar) && (
                  <Menu.Item key="agregarEquipo" icon={<PlusOutlined />}>
                    <Link to="/sistema/equipo/agregar">Agregar</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.recursos_humanos) && (
              <Menu.SubMenu
                key="recursosHumanos"
                icon={<RHIcon />}
                title="Rec. Humanos"
              >
                {(isAdmin || permisos.recursos_humanos.solicitudes) && (
                  <Menu.SubMenu
                  key="solicitudRH"
                  icon={<HolidayIcon />}
                  title="Solicitudes"
                  >
                  {(isAdmin || permisos.recursos_humanos.solicitudes.buscar) && (
                  <Menu.Item key="buscarSolicitudRH" icon={<SearchOutlined />}>
                    <Link to="/sistema/solicitud/buscar">Buscar</Link>
                  </Menu.Item>
                  )}
                  {(isAdmin || permisos.recursos_humanos.solicitudes.agregar) && (
                  <Menu.Item key="agregarSolicitudRH" icon={<PlusOutlined />}>
                    <Link to="/sistema/solicitud/agregar">Agregar</Link>
                  </Menu.Item>
                  )}
                </Menu.SubMenu>
                )}
                {(isAdmin || permisos.recursos_humanos.asistencias) && (
                  <Menu.SubMenu
                  key="asistencias"
                  icon={<ClockCircleOutlined />}
                  title="Asistencias"
                  >
                    {(isAdmin || permisos.recursos_humanos.asistencias.buscar) && (
                  <Menu.Item key="buscarAsistencia" icon={<SearchOutlined />}>
                    <Link to="/sistema/asistencia/buscar">Buscar</Link>
                  </Menu.Item>
                )}
                    {(isAdmin || permisos.recursos_humanos.asistencias.agregar) && (
                  <Menu.Item key="agregarAsistencia" icon={<PlusOutlined />}>
                    <Link to="/sistema/asistencia/agregar">Agregar</Link>
                  </Menu.Item>
                )}
                </Menu.SubMenu>
                )}
              </Menu.SubMenu>
            )}
            {(isAdmin || permisos.configuracion) && (
              <>
                <Menu.SubMenu
                  key="configuracion"
                  icon={<SettingOutlined />}
                  title="Configuración"
                >
                  <Menu.Item key="configurarPuestos" icon={<PositionIcon />}>
                    <Link to="/sistema/configurar/puestos">Puestos</Link>
                  </Menu.Item>
                  <Menu.Item key="configurarClientes" icon={<PermissionIcon />}>
                    <Link to="/sistema/configurar/clientes">Clientes</Link>
                  </Menu.Item>
                  <Menu.Item key="configurarNotificaciones" icon={<BellOutlined />}>
                    <Link to="/sistema/configurar/notificaciones">Notificaciones</Link>
                  </Menu.Item>
                </Menu.SubMenu>
              </>
            )}
            <Menu.Item key="logout" icon={<LogoutOutlined />}>
              <Link onClick={handleLogout}>Salir</Link>
            </Menu.Item>
          </Menu>
        </Sider>
        <Layout>
          <Header
            className="ccg-header"
            style={{
              padding: 0,
              paddingTop: "25px",
              paddingRight: "40px",
              color: "white",
              backgroundColor: "#283891",
            }}
          >
            <NotificationBell />
          </Header>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <Spin size="large" tip="Cargando permisos..." />
            </div>
          ) : (
            (isAdmin || permisos) ? (
              <>
                <Content
                  style={{
                    margin: "0",
                    maxHeight: "85vh",
                    overflowY: "scroll",
                    padding: "0px 25px",
                  }}
                >
                  <Breadcrumb className="ccg-colors">
                    <Breadcrumb.Item>Bienvenido</Breadcrumb.Item>
                    <Breadcrumb.Item>{role}</Breadcrumb.Item>
                    <Breadcrumb.Item>{currentUser.displayName}</Breadcrumb.Item>
                  </Breadcrumb>
                  {isBirthday && (
                    <Alert
                      message={`¡Feliz cumpleaños, ${currentUser.displayName}!`}
                      description="Esperamos que tengas un día maravilloso. Gracias por ser parte de CCG S.A de C.V."
                      type="success"
                      showIcon
                    />
                  )}
                  <Outlet />
                </Content>
              </>
            ) : (
              <NoPermisos />
            )
          )}
          <Footer className="ccg-footer-container">
              CONTROL DE CALIDAD<br />Y GEOTÉCNIA S.A DE C.V
          </Footer>
        </Layout>
      </Layout>
      </NotificacionesProvider>
    </NotificationProvider>
  );
};

export default AdminLayout;
